import React, { useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  Typography,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';

const OwnPredictions = ({ authHeaders, backEnd }) => {
  const theme = useTheme();
  const [predictions, setPredictions] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [orderBy, setOrderBy] = useState('');
  const [order, setOrder] = useState('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [selectedPrediction, setSelectedPrediction] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Helper function to format values (0 as "Не", 1 as "Да")
  const formatValue = (value) => {
    return value === 1 ? 'Да' : value === 0 ? 'Не' : value;
  };

  useEffect(() => {
    const fetchPredictions = async () => {
      try {
        const response = await fetch(backEnd + 'list_own_predictions', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: authHeaders },
          body: JSON.stringify({ action: 'extract_predictions' }),
        });

        if (!response.ok) {
          throw new Error('Failed to fetch predictions');
        }

        const data = await response.json();
        if (Array.isArray(data)) {
          setPredictions(data);
          setFilteredData(data);  // Ensure that filteredData is always an array
        } else {
          console.error('Fetched data is not an array', data);
          setPredictions([]);
          setFilteredData([]);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchPredictions();
  }, [authHeaders, backEnd]);

  const handleSearch = (event) => {
    const query = event.target.value.toLowerCase();
    setSearchQuery(query);
    setFilteredData(
      predictions.filter((prediction) =>
        Object.values(prediction).some((value) =>
          String(value).toLowerCase().includes(query)
        )
      )
    );
  };

  const handleSort = (column) => {
    const isAsc = orderBy === column && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(column);

    const sortedData = [...filteredData].sort((a, b) => {
      if (a[column] < b[column]) return isAsc ? -1 : 1;
      if (a[column] > b[column]) return isAsc ? 1 : -1;
      return 0;
    });

    setFilteredData(sortedData);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowClick = (prediction) => {
    setSelectedPrediction(prediction);
    setDialogOpen(true);
  };

  const columns = ['Град', 'Квартал', 'Площ', 'Стаи', 'AI оценка (€)', 'Етаж', 'Общо етажи'];
  const columnsLimited = columns;

  return (
    <Box sx={{ maxWidth: '95%', margin: 'auto', mt: 4 }}>
      <Typography variant="h5" gutterBottom sx={{ color: theme.palette.text.primary, fontWeight: 'bold', textAlign: 'center' }}>
        История на AI оценките
      </Typography>

      <TextField
        fullWidth
        label="Търсене"
        variant="outlined"
        value={searchQuery}
        onChange={handleSearch}
        sx={{ mb: 2 }}
      />

      <TableContainer component={Paper} sx={{ overflowX: 'auto', maxWidth: '100%' }}>
        <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
          <TableHead>
            <TableRow>
              {columnsLimited.map((column) => (
                <TableCell key={column} sx={{ whiteSpace: 'nowrap', maxWidth: 200, fontSize: '10px' }}>
                  <TableSortLabel
                    active={orderBy === column}
                    direction={orderBy === column ? order : 'asc'}
                    onClick={() => handleSort(column)}
                  >
                    {column}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.isArray(filteredData) &&
              filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((prediction) => (
                <TableRow key={prediction.id} onClick={() => handleRowClick(prediction)} sx={{ cursor: 'pointer' }}>
                  {columnsLimited.map((column) => (
                    <TableCell key={column} sx={{ wordBreak: 'break-word', maxWidth: 150, fontSize: '12px' }}>
                      {formatValue(prediction[column])}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 20, 50]}
        component="div"
        count={Array.isArray(filteredData) ? filteredData.length : 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Детайли за предвиждането</DialogTitle>
        <DialogContent>
          {selectedPrediction && Object.keys(selectedPrediction).map((column) => (
            <Typography key={column} sx={{ marginBottom: 1 }}>
              <strong>{column}:</strong> {formatValue(selectedPrediction[column])}
            </Typography>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Затвори</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OwnPredictions;
